import { coerceDecimal } from '@phensley/decimal';
import { DateTimePatternField } from '@phensley/cldr-types';
import { Cache } from '@phensley/cldr-utils';
import { CalendarPatterns, GregorianPatterns } from './patterns';
var maskedFOVDFields = [
    0 /* Field.ERA */,
    1 /* Field.YEAR */,
    3 /* Field.MONTH */,
    7 /* Field.DAY */,
    10 /* Field.DAYPERIOD */,
    11 /* Field.HOUR */,
    12 /* Field.MINUTE */,
    13 /* Field.SECOND */,
];
var minutes = function (d) { return d.hourOfDay() * 60 + d.minute(); };
var CalendarManager = /** @class */ (function () {
    function CalendarManager(bundle, internals) {
        var _this = this;
        this.bundle = bundle;
        this.internals = internals;
        // calendars config array should always be non-empty
        this.availableCalendars = new Set(internals.config.calendars || []);
        var schema = internals.schema;
        this.patternCache = new Cache(function (calendar) {
            if (_this.availableCalendars.has(calendar)) {
                switch (calendar) {
                    case 'buddhist':
                        return new CalendarPatterns(bundle, internals, schema.Buddhist);
                    case 'japanese':
                        return new CalendarPatterns(bundle, internals, schema.Japanese);
                    case 'persian':
                        return new CalendarPatterns(bundle, internals, schema.Persian);
                }
            }
            return new GregorianPatterns(bundle, internals, schema.Gregorian);
        }, 20);
    }
    CalendarManager.prototype.getCalendarPatterns = function (calendar) {
        return this.patternCache.get(calendar);
    };
    CalendarManager.prototype.getDateFormatRequest = function (date, options, params) {
        var calendar = this.internals.calendars.selectCalendar(this.bundle, options.ca);
        var patterns = this.getCalendarPatterns(calendar);
        var dateKey = this.supportedOption(options.datetime || options.date);
        var timeKey = this.supportedOption(options.datetime || options.time);
        var wrapKey = this.supportedOption(options.wrap);
        var skelKey = options.skeleton || '';
        if (!dateKey && !timeKey && !skelKey) {
            dateKey = 'long';
        }
        var atTime = options.atTime === false ? false : true;
        var wrapper = '';
        if (wrapKey) {
            wrapper = patterns.getWrapperPattern(wrapKey, atTime);
        }
        else if (dateKey && timeKey) {
            wrapper = patterns.getWrapperPattern(dateKey, atTime);
        }
        var req = { wrapper: wrapper, params: params };
        if (dateKey) {
            req.date = patterns.getDatePattern(dateKey);
        }
        if (timeKey) {
            req.time = patterns.getTimePattern(timeKey);
        }
        // We have both standard formats, we're done
        if (req.date && req.time) {
            return req;
        }
        // We have at least a date/time standard format.
        if (req.date || req.time) {
            // If no skeleton specified, we're done
            if (!skelKey) {
                return req;
            }
        }
        // Cache best-fit results per raw skeleton, plus flags for which
        // standard date/time formats and wrapper are also in play. Mixing
        // a standard format with a skeleton changes the result for the
        // same skeleton, so the flags are part of the key. The original
        // canonical skeleton key collapsed field widths (e.g. 'SS' to 'S'),
        // which changed the adjusted output, so the raw skeleton is used.
        //
        // The resolved pattern also depends on the date for plural-category-
        // dependent skeletons (e.g. 'yw'/'MMMMW' pick a per-plural-category
        // pattern -- see CalendarPatterns.getAvailablePattern) and on the
        // numbering system, since fractional-second adjustment embeds
        // params.symbols.decimal in the nodes. Both are included in the
        // cache key (claude-review.md bugs 1-2).
        var pluralKey = '';
        if (!req.date && /[wW]/.test(skelKey)) {
            var p = this.bundle.plurals();
            var wkM = p.cardinal(coerceDecimal(date.weekOfMonth()));
            var wkY = p.cardinal(coerceDecimal(date.weekOfYear()));
            pluralKey = "".concat(wkM, "\u0001").concat(wkY);
        }
        var cacheKey = "".concat(skelKey, "\t").concat(dateKey, "\t").concat(timeKey, "\t").concat(wrapKey, "\t").concat(pluralKey, "\t").concat(params.numberSystemName);
        var dateSkel;
        var entry = patterns.getCachedSkeletonRequest(cacheKey);
        if (entry) {
            // Reuse the previously matched patterns
            req.date = entry.date;
            req.time = entry.time;
            dateSkel = entry.dateSkel;
        }
        else {
            // No cached entry, so parse the skeleton and perform a best-fit
            // match against all available formats.
            var query = void 0;
            // We have a standard date or time pattern along with a skeleton.
            // We split the skeleton into date/time parts, then use the one
            // that doesn't conflict with the specified standard format.
            if (req.date || req.time) {
                query = patterns.parseSkeleton(skelKey);
                var time = query.split();
                if (req.date) {
                    query = time;
                }
            }
            else {
                // No standard format specified, so just parse the skeleton
                query = patterns.parseSkeleton(skelKey);
            }
            var timeQuery = void 0;
            var timeSkel = void 0;
            // Check if skeleton specifies date or time fields, or both.
            if (query.compound()) {
                // Separate into a date and a time skeletons.
                timeQuery = query.split();
                dateSkel = patterns.matchAvailable(query);
                timeSkel = patterns.matchAvailable(timeQuery);
            }
            else if (query.isDate) {
                dateSkel = patterns.matchAvailable(query);
            }
            else {
                timeQuery = query;
                timeSkel = patterns.matchAvailable(query);
            }
            if (dateSkel) {
                req.date = this.getAvailablePattern(patterns, date, query, dateSkel, params);
            }
            if (timeQuery && timeSkel) {
                req.time = this.getAvailablePattern(patterns, date, timeQuery, timeSkel, params);
            }
            patterns.setCachedSkeletonRequest(cacheKey, { date: req.date, time: req.time, dateSkel: dateSkel });
        }
        if (!wrapKey) {
            if (dateSkel && req.date && req.time) {
                // Select wrapper based on fields in date skeleton
                req.wrapper = this.selectWrapper(patterns, dateSkel, req.date, atTime);
            }
            else {
                // Select wrapper based on width of standard date format
                req.wrapper = patterns.getWrapperPattern(dateKey || 'short', atTime);
            }
        }
        return req;
    };
    /**
     * Formats datetime intervals.
     *
     * NOTE: The ICU implementation of CLDR datetime range formatting
     * contains inconsistent behavior (see code at end of comment below).
     *
     * The skeleton data for interval formatting consists of a series of
     * separate date and time patterns. No interval skeletons / formats
     * contain both date and time fields.
     *
     * For example, the data might contain the following:
     *
     *  ['y', 'yM', 'yMd', 'h', 'hm', 'H', 'Hm']
     *
     * However a caller can pass input skeleton containing a mix of date and
     * time fields. We split this skeleton into separate date and time
     * skeletons, and perform formatting by considering the possibilities below.
     *
     * Variables:
     *
     *   start         = start datetime
     *   end           = end datetime
     *   skeleton      = input skeleton
     *   fovd          = fieldOfVisualDifference(start, end)
     *   date_differs  = fovd in ('era', 'year', 'month', 'day')
     *   time_differs  = fovd in ('dayperiod', 'hour', 'minute')
     *   equal         = fovd in ('second', undefined)
     *
     * Formatting Rules:
     *
     * 1. Skeleton requests both date and time fields
     *
     *   a. IF time_differs, format date followed by time range
     *      e.g. "date, time0 - time 1"
     *
     *   b. ELSE IF date_differs, format generic fallback
     *      e.g. "date0, time0 - date1, time1"
     *
     *   c. ELSE format the date + time standalone
     *      e.g. "date0, time0"
     *
     * 2: Skeleton only requests date fields
     *
     *   a. IF date_differs, format date range
     *      e.g. "date0 - date1"
     *
     *   b. ELSE format date standalone
     *
     * 3: Skeleton only requests time fields
     *
     *   a. IF time_differs, format time range
     *      e.g. "time0 - time1"
     *
     *   b. ELSE format time standalone
     *
     * ========================================================
     *
     * Example of inconsistency of ICU range formatting.
     * The output was produced using Node 23.7.0 and ICU 74.
     * ICU4J version 75 produces the same output.
     *
     *     const OPTS = [
     *       { day: 'numeric' },
     *       { day: 'numeric', minute: '2-digit' }
     *     ];
     *     const start = new Date(Date.UTC(2007, 0, 1, 10, 12, 0));
     *     const end = new Date(Date.UTC(2008, 0, 2, 11, 13, 0));
     *     for (let i = 0; i < OPTS.length; i++) {
     *       const opts = OPTS[i];
     *       const fmt = new Intl.DateTimeFormat('en', opts);
     *       console.log(fmt.formatRange(start, end));
     *     }
     *
     * This code formats two dates two different ways:
     *
     *   2007-Jan-01 10:12
     *   2008-Jan-02 11:13
     *
     * 1. Display the day: { day: 'numeric' }
     *
     *  ICU auto-expands the selected pattern to include the year
     *  and month:
     *
     *    "1/1/2007 – 1/2/2008"
     *
     *  This adds context needed to understand the two
     *  dates are separated by 367 days.
     *
     * 2. Display day and minute: { day: 'numeric', minute: '2-digit' }
     *
     *  No pattern expansion occurs, we get only what we
     *  requested. The output is highly ambiguous since it's
     *  missing year, month, and hour fields:
     *
     *    "1, 12 – 2, 13"
     *
     * Additional context is added in one case but not in the other.
     *
     * IMO it makes more sense to leave the input skeleton as untouched
     * as possible, leaving it up to the caller to decide which
     * fields to request.
     */
    CalendarManager.prototype.getDateIntervalFormatRequest = function (calendar, start, end, options, params) {
        var patterns = this.getCalendarPatterns(calendar);
        var wrapper = patterns.getIntervalFallback();
        var req = { params: params, wrapper: wrapper };
        // Determine whether the largest field of visual difference (fovd)
        // is a date or time field, or neither.  Note that interval patterns
        // in the CLDR data do not include seconds, so we consider dates
        // that only differ in seconds to be equivalent.
        var fovd = start.fieldOfVisualDifference(end) || 's';
        var dateDiffers = 'GyMd'.indexOf(fovd) !== -1;
        var timeDiffers = 'BahHm'.indexOf(fovd) !== -1;
        // If main skeleton input is not used, select either date or
        // time based on whether the date or time differ.
        var skeleton = options.skeleton;
        if (!skeleton) {
            if (dateDiffers && options.date) {
                skeleton = options.date;
            }
            else {
                skeleton = options.time;
            }
        }
        // If no skeleton is defined, choose a simple default
        var defaulted = false;
        if (!skeleton) {
            skeleton = dateDiffers ? 'yMMMd' : 'jm';
            defaulted = true;
        }
        // At this point the skeleton contains at least one field.
        // Parse the input skeleton.
        var query = patterns.parseSkeleton(skeleton);
        // If we are not using a default pattern, check a few cases and
        // augment the skeleton accordingly.
        if (!defaulted) {
            // In non-strict mode, if the query requested time fields only
            // and the date fields differ, insert some context.
            if (!options.strict && dateDiffers && !query.isDate) {
                skeleton = "yMMMd" + skeleton;
                query = patterns.parseSkeleton(skeleton);
            }
            // Interval skeletons for bare seconds 's' and minutes 'm' do not
            // exist in the CLDR data. We fill in the gap to ensure we at least
            // match on the correct hour field for the current locale.
            var largest = this.largestSkeletonField(query);
            if ('sm'.indexOf(largest) !== -1) {
                skeleton = (largest === 's' ? 'jm' : 'j') + skeleton;
                query = patterns.parseSkeleton(skeleton);
            }
        }
        // BEGIN formatting rules.
        // RULE 1. Skeleton contains both date and time fields
        if (query.compound()) {
            if (timeDiffers) {
                // RULE 1a. IF time_differs, format date followed by time range
                var timeQuery = query.split();
                req.date = this.matchAvailablePattern(patterns, start, query, params);
                query = timeQuery;
                // ... (1a) intentional fall through to format date + time range
            }
            else if (dateDiffers) {
                // RULE 1b. ELSE IF date_differs, format generic fallback
                req.skeleton = skeleton;
                return req;
            }
            else {
                // RULE 1c. ELSE format the date + time standalone
                var timeQuery = query.split();
                req.date = this.matchAvailablePattern(patterns, start, query, params);
                req.time = this.matchAvailablePattern(patterns, start, timeQuery, params);
                return req;
            }
        }
        // RULE 2: skeleton only contains date fields
        // RULE 2b ELSE format date standalone (!dateDiffers)
        if (!dateDiffers && !query.isTime) {
            req.date = this.matchAvailablePattern(patterns, start, query, params);
            return req;
        }
        // Something differs in date or time fields
        if (fovd !== 's') {
            // RULE 2a IF dateDiffers, format date range
            // RULE 3a IF timeDiffers, format time range
            var match = patterns.matchInterval(query);
            if (match && match.data) {
                // Compute masked field of visual difference using the found skeleton.
                var fovd_1 = this.maskedFOVD(start, end, match.skeleton);
                // Use fovd to select final pattern. Since it was masked by the matched
                // skeleton, the fovd should completely cover the set of patterns in the data.
                var pattern = match.data.patterns[fovd_1];
                var parsedPattern = this.internals.calendars.parseDatePattern(pattern || '');
                /* istanbul ignore else -- @preserve */
                if (parsedPattern.length) {
                    req.range = patterns.adjustPattern(parsedPattern, query, match.skeleton, params.symbols.decimal);
                }
            }
        }
        else {
            // RULE 3b ELSE format time standalone
            req.date = this.matchAvailablePattern(patterns, start, query, params);
        }
        return req;
    };
    CalendarManager.prototype.largestSkeletonField = function (skeleton) {
        for (var _i = 0, maskedFOVDFields_1 = maskedFOVDFields; _i < maskedFOVDFields_1.length; _i++) {
            var field = maskedFOVDFields_1[_i];
            var info = skeleton.info[field];
            if (info) {
                return info.field;
            }
        }
        return DateTimePatternField.SECOND;
    };
    CalendarManager.prototype.maskedFOVD = function (start, end, skeleton) {
        var smallest;
        for (var _i = 0, maskedFOVDFields_2 = maskedFOVDFields; _i < maskedFOVDFields_2.length; _i++) {
            var field = maskedFOVDFields_2[_i];
            var info = skeleton.info[field];
            if (!info) {
                continue;
            }
            smallest = info;
            switch (field) {
                case 0 /* Field.ERA */:
                    if (start.era() !== end.era()) {
                        return DateTimePatternField.ERA;
                    }
                    break;
                case 1 /* Field.YEAR */:
                    if (start.year() !== end.year()) {
                        return DateTimePatternField.YEAR;
                    }
                    break;
                case 3 /* Field.MONTH */:
                    if (start.month() !== end.month()) {
                        return DateTimePatternField.MONTH;
                    }
                    break;
                case 7 /* Field.DAY */:
                    if (start.dayOfMonth() !== end.dayOfMonth()) {
                        return DateTimePatternField.DAY;
                    }
                    break;
                case 10 /* Field.DAYPERIOD */:
                    switch (info.field) {
                        case 'a': // dayperiod
                        case 'b': // dayperiod extended (resolve to 'a' for now)
                            if (start.isAM() !== end.isAM()) {
                                return DateTimePatternField.DAYPERIOD;
                            }
                            break;
                        case 'B': // dayperiod flex
                            if (this.dayperiodFlex(start) !== this.dayperiodFlex(end)) {
                                return DateTimePatternField.DAYPERIOD_FLEX;
                            }
                            break;
                    }
                    break;
                case 11 /* Field.HOUR */:
                    switch (info.field) {
                        case 'h': // hour 1-12
                        case 'K': // hour 0-11
                            if (start.hour() !== end.hour()) {
                                return DateTimePatternField.HOUR12;
                            }
                            break;
                        case 'H': // hour 0-23
                        case 'k': // hour 1-24
                            if (start.hourOfDay() !== end.hourOfDay()) {
                                return DateTimePatternField.HOUR24;
                            }
                            break;
                    }
                    break;
                case 12 /* Field.MINUTE */:
                case 13 /* Field.SECOND */:
                    if (start.minute() !== end.minute()) {
                        return DateTimePatternField.MINUTE;
                    }
                    break;
            }
        }
        // If we exhaust all fields of the skeleton, the two dates are equivalent
        // with respect to the fields masked by the skeleton. We return the smallest
        // field by default.
        return smallest ? smallest.field : DateTimePatternField.MINUTE;
    };
    CalendarManager.prototype.dayperiodFlex = function (date) {
        return this.internals.calendars.flexDayPeriod(this.bundle, minutes(date)) || '';
    };
    CalendarManager.prototype.matchAvailablePattern = function (patterns, date, query, params) {
        var match = patterns.matchAvailable(query);
        return this.getAvailablePattern(patterns, date, query, match, params);
    };
    CalendarManager.prototype.getAvailablePattern = function (patterns, date, query, match, params) {
        var pattern = patterns.getAvailablePattern(date, match);
        /* istanbul ignore else -- @preserve */
        if (pattern.length) {
            return patterns.adjustPattern(pattern, query, match, params.symbols.decimal);
        }
        // Base standard calendar formats are (currently) always defined.
        /* istanbul ignore next -- @preserve */
        return undefined;
    };
    /**
     * Select appropriate wrapper based on fields in the date skeleton.
     */
    CalendarManager.prototype.selectWrapper = function (patterns, dateSkel, _date, atTime) {
        var wrapKey = 'short';
        var monthWidth = dateSkel.monthWidth();
        var hasWeekday = dateSkel.has(6 /* Field.WEEKDAY */);
        if (monthWidth === 4) {
            wrapKey = hasWeekday ? 'full' : 'long';
        }
        else if (monthWidth === 3) {
            wrapKey = 'medium';
        }
        return patterns.getWrapperPattern(wrapKey, atTime);
    };
    CalendarManager.prototype.supportedOption = function (key) {
        switch (key) {
            case 'full':
            case 'long':
            case 'medium':
            case 'short':
                return key;
            default:
                return '';
        }
    };
    return CalendarManager;
}());
export { CalendarManager };
//# sourceMappingURL=manager.js.map